import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Printer, Building2, ShoppingCart, Package, Calendar, User, Box } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 20;

const formatDT = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const DirectShipHistory = () => {
  const navigate = useNavigate();

  const [filter, setFilter] = useState('all'); // 'all' | 'site' | 'customer'
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  const [boxes, setBoxes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reprinting, setReprinting] = useState({}); // boxNumber -> bool

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filter !== 'all') params.destination_type = filter;
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await apiClient.get('/assortment-ops/ship/history', { params });
      setBoxes(r.data.boxes || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [filter, search, dateFrom, dateTo, page]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') { setPage(0); fetchPage(); }
  };

  const setFilterAndReset = (f) => { setFilter(f); setPage(0); };

  const handleReprint = async (box) => {
    setReprinting(prev => ({ ...prev, [box.box_number]: true }));
    try {
      const r = await apiClient.get(
        `/assortment-ops/ship/box/${encodeURIComponent(box.box_number)}/packing-slip`,
        { responseType: 'blob' }
      );
      const pdfBlob = r.data.type ? r.data : new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.download = `packing_slip_${box.box_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(`Slip for ${box.box_number} downloaded`);
    } catch (err) {
      const status = err.response?.status;
      let detail = err.response?.data?.detail;
      if (!detail && err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail; } catch { /* ignore */ }
      }
      toast.error(status ? `Reprint failed (${status}): ${detail || err.message}` : `Reprint failed: ${detail || err.message}`);
    } finally {
      setReprinting(prev => ({ ...prev, [box.box_number]: false }));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  const countsByFilter = {
    all: total,
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="direct-ship-history-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse/direct-ship')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Direct Ship History</h1>
          <p className="text-xs text-gray-500">View and reprint Site Transfer / Customer Sale packing slips</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="pt-4 space-y-3">
            {/* Tabs */}
            <div className="flex rounded-lg border overflow-hidden">
              <button
                onClick={() => setFilterAndReset('all')}
                className={`flex-1 py-2 px-2 text-sm font-medium ${filter === 'all' ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                data-testid="filter-all"
              >All</button>
              <button
                onClick={() => setFilterAndReset('site')}
                className={`flex-1 py-2 px-2 text-sm font-medium flex items-center justify-center gap-1.5 ${filter === 'site' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                data-testid="filter-site"
              >
                <Building2 className="w-4 h-4" /> Site Transfer
              </button>
              <button
                onClick={() => setFilterAndReset('customer')}
                className={`flex-1 py-2 px-2 text-sm font-medium flex items-center justify-center gap-1.5 ${filter === 'customer' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                data-testid="filter-customer"
              >
                <ShoppingCart className="w-4 h-4" /> Customer Sale
              </button>
            </div>

            {/* Search + date range */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search box # / destination / pick slip"
                  className="pl-8"
                  data-testid="search-input"
                />
              </div>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                data-testid="date-from-input"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                data-testid="date-to-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <div className="space-y-2">
          {loading && <p className="text-sm text-gray-500 text-center py-8">Loading…</p>}
          {!loading && boxes.length === 0 && (
            <Card><CardContent className="py-12 text-center text-gray-500 text-sm">
              <Box className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No shipments found for the selected filters
            </CardContent></Card>
          )}
          {!loading && boxes.map((b) => {
            const isSite = b.destination_type === 'site';
            const TypeIcon = isSite ? Building2 : ShoppingCart;
            const typeColor = isSite ? 'blue' : 'green';
            return (
              <Card key={b.box_number} className="hover:shadow-sm transition-shadow" data-testid={`ship-row-${b.box_number}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg bg-${typeColor}-50 text-${typeColor}-600 flex items-center justify-center shrink-0`}>
                      <TypeIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <p className="font-mono font-bold text-sm">{b.box_number}</p>
                        <Badge className={`text-[10px] ${isSite ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {isSite ? 'Site Transfer' : 'Customer Sale'}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium mt-0.5 truncate">{b.destination_name || '-'}</p>
                      {b.pick_slip_number && (
                        <p className="text-xs text-gray-500 font-mono">PS #{b.pick_slip_number}</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-gray-600">
                        <span className="flex items-center gap-1"><Package className="w-3 h-3" />{b.total_bundles || 0} bundles · {b.total_usns || 0} USNs</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDT(b.created_at)}</span>
                        {b.created_by_name && (
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{b.created_by_name}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReprint(b)}
                      disabled={!!reprinting[b.box_number]}
                      data-testid={`reprint-${b.box_number}`}
                    >
                      <Printer className="w-3.5 h-3.5 mr-1.5" />
                      {reprinting[b.box_number] ? 'Preparing…' : 'Reprint'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-gray-500">Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={!canPrev} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="prev-page">Prev</Button>
              <Button variant="outline" size="sm" disabled={!canNext} onClick={() => setPage(p => p + 1)} data-testid="next-page">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectShipHistory;
